import { useCallback, useEffect, useRef, type RefObject } from 'react';

/** One finite compositor animation per interaction; no pointer-move renders or page loop. */
export function useCardRollover(scene: RefObject<HTMLElement | null>, disabled: boolean) {
  const running = useRef<Animation[]>([]);
  const cancel = useCallback(() => {
    running.current.forEach((animation) => animation.cancel());
    running.current = [];
  }, []);
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const changed = () => {
      if (media?.matches) cancel();
    };
    media?.addEventListener('change', changed);
    return () => {
      media?.removeEventListener('change', changed);
      cancel();
    };
  }, [cancel]);
  useEffect(() => {
    if (disabled) cancel();
  }, [disabled, cancel]);
  return useCallback(() => {
    if (disabled || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    cancel();
    const face = scene.current?.querySelector('.pc-card-face[aria-hidden="false"]');
    const sweep = face?.querySelector<HTMLElement>('.ct-card-sweep');
    const rim = face?.querySelector<HTMLElement>('.ct-card-rim');
    if (!sweep?.animate || !rim?.animate) return;
    const options = { duration: 760, easing: 'cubic-bezier(.22,.61,.36,1)' };
    running.current = [
      sweep.animate(
        [
          { transform: 'translateX(-160%) rotate(18deg)', opacity: 0 },
          { transform: 'translateX(30%) rotate(18deg)', opacity: 0.5, offset: 0.45 },
          { transform: 'translateX(360%) rotate(18deg)', opacity: 0 },
        ],
        options
      ),
      rim.animate([{ opacity: 0 }, { opacity: 0.8, offset: 0.35 }, { opacity: 0 }], options),
    ];
  }, [scene, disabled, cancel]);
}
